import React, { useEffect, useMemo, useState } from "react";
import { useQuery } from "@apollo/client";
import { makeStyles } from "@material-ui/core/styles";
import authManager from "../../../../lib/authManager";
import { listUsersQuery } from "../../../Settings/Users/queries";
import { T } from "../../constants";
import { useUi, cx } from "../../ui";
import { toDateInput, addDays, nightsBetween, nextVoucherId } from "../data";
import Overlay from "../../components/Overlay";

/* ---------- Allocate a ticket to a pre-booking, or create a walk-in ---------- */

const useStyles = makeStyles({
  dialog: { width: "100%", maxWidth: 560, borderRadius: 12, border: `1px solid ${T.border}`, background: T.elevated, padding: 24, boxShadow: "0 20px 50px rgba(0,0,0,0.25)" },
  head: { display: "flex", alignItems: "flex-start", justifyContent: "space-between", gap: 12 },
  heading: { fontSize: 20, fontWeight: 700, color: T.fg },
  sub: { marginTop: 4, fontSize: 12, color: T.mutedFg },
  closeBtn: { border: "none", background: "transparent", fontSize: 14, color: T.mutedFg, cursor: "pointer", fontFamily: "inherit" },
  segmented: { marginTop: 16, display: "inline-flex", borderRadius: 8, border: `1px solid ${T.border}`, background: T.surface, padding: 2 },
  segBtn: { borderRadius: 6, padding: "6px 12px", fontSize: 12, fontWeight: 600, border: "none", cursor: "pointer", fontFamily: "inherit", background: "transparent", color: T.mutedFg },
  segOn: { background: T.elevated, color: T.fg, boxShadow: "0 2px 6px rgba(0,0,0,0.08)" },
  stack: { marginTop: 16, display: "flex", flexDirection: "column", gap: 12 },
  grid2: { display: "grid", gridTemplateColumns: "1fr 1fr", gap: 12 },
  label: { display: "block", fontSize: 11, textTransform: "uppercase", letterSpacing: 0.5, color: T.subtleFg },
  field: { marginTop: 4 },
  lockCard: { borderRadius: 6, border: `1px solid ${T.border}`, background: T.surface, padding: "10px 12px" },
  lockName: { marginTop: 2, fontWeight: 600, color: T.fg },
  lockMeta: { fontSize: 12, color: T.mutedFg },
  plainValue: { marginTop: 4, fontSize: 14, color: T.fg },
  emptyNote: { borderRadius: 6, border: `1px dashed ${T.border}`, background: T.surface, padding: "10px 12px", fontSize: 12, color: T.mutedFg },
  checkRow: { marginTop: 8, display: "flex", alignItems: "center", gap: 8, fontSize: 12, color: T.mutedFg },
  summary: { display: "flex", alignItems: "center", justifyContent: "space-between", borderRadius: 6, background: T.surface, padding: "8px 12px", fontSize: 12, color: T.mutedFg },
  footer: { marginTop: 8, display: "flex", justifyContent: "flex-end", gap: 8 },
  cancelBtn: { border: "none", background: "transparent" },
});

const toInputDate = (value) => {
  if (!value) return "";

  if (value instanceof Date) return toDateInput(value);

  return String(value).slice(0, 10);
};

const hasFacilityRole = (user, installationId) =>
  Boolean(
    installationId &&
      user?.userRoles?.some((userRole) => userRole?.installationId === installationId),
  );

const toEmployeeLabel = (employee) =>
  employee?.name || employee?.email || "Unknown staff member";

export default function IssueDialog({
  lots,
  awaiting,
  initial,
  defaultLotId,
  installationId,
  allocating,
  cancelLabel = "Cancel",
  allowUnknownPlate = true,
  onCancel,
  onAllocate,
  onCreateWalkIn,
}) {
  const s = useStyles();
  const ui = useUi();
  const { data: usersData, loading: usersLoading } = useQuery(listUsersQuery, {
    fetchPolicy: "no-cache",
  });

  const [mode, setMode] = useState(initial.walkIn ? "walkin" : "booking");

  const preselected = initial.booking || (initial.bookingRef ? awaiting.find((a) => a.ref === initial.bookingRef) : undefined);
  const isLocked = !!preselected;

  const [bookingRef, setBookingRef] = useState(preselected?.ref ?? awaiting[0]?.ref ?? "");
  const selectedBooking = preselected || awaiting.find((a) => a.ref === bookingRef);

  const [lotId, setLotId] = useState(selectedBooking?.lotId ?? defaultLotId ?? lots[0]?.id ?? "");
  const [guestName, setGuestName] = useState("");
  const [email, setEmail] = useState("");
  const [plate, setPlate] = useState(selectedBooking?.plate ?? "");
  const [plateUnknownInput, setPlateUnknown] = useState(!selectedBooking?.plate);
  const plateUnknown = allowUnknownPlate ? plateUnknownInput : false;
  const today = toDateInput(new Date());
  const tomorrow = toDateInput(addDays(new Date(), 1));
  const [checkIn, setCheckIn] = useState(toInputDate(selectedBooking?.startDay || selectedBooking?.checkIn) || today);
  const [checkOut, setCheckOut] = useState(toInputDate(selectedBooking?.endDay || selectedBooking?.checkOut) || tomorrow);
  const [issuedBy, setIssuedBy] = useState(authManager.userId || "");
  const [note, setNote] = useState("");

  const employees = useMemo(() => {
    const enabledUsers = (usersData?.allUsers || []).filter((user) => user?.isEnabled !== false);
    const facilityUsers = enabledUsers.filter((user) => hasFacilityRole(user, installationId));
    const currentUser = authManager.currentUser;

    if (!currentUser?.id || facilityUsers.some((user) => user.id === currentUser.id)) {
      return facilityUsers;
    }

    return [currentUser, ...facilityUsers];
  }, [installationId, usersData]);

  useEffect(() => {
    if (mode !== "booking" || !selectedBooking) return;
    setPlate(selectedBooking.plate ?? "");
    setPlateUnknown(!selectedBooking.plate);
    setCheckIn(toInputDate(selectedBooking.startDay || selectedBooking.checkIn) || today);
    setCheckOut(toInputDate(selectedBooking.endDay || selectedBooking.checkOut) || tomorrow);
    setLotId(selectedBooking.lotId ?? defaultLotId ?? lots[0]?.id ?? "");
  }, [bookingRef, defaultLotId, lots, mode, selectedBooking, today, tomorrow]);

  useEffect(() => {
    if (!employees.length) return;

    const currentUserId = authManager.userId;
    const selectedEmployeeExists = employees.some((employee) => employee.id === issuedBy);

    if (issuedBy && selectedEmployeeExists) {
      return;
    }

    if (currentUserId && employees.some((employee) => employee.id === currentUserId)) {
      setIssuedBy(currentUserId);
      return;
    }

    setIssuedBy(employees[0].id);
  }, [employees, issuedBy]);

  const selectedLot = lots.find((l) => l.id === lotId) || (
    selectedBooking?.lotId
      ? {
          id: selectedBooking.lotId,
          name: selectedBooking.lotName,
          type: selectedBooking.lotType,
        }
      : undefined
  );
  const remaining = selectedLot?.available ?? 0;
  const nights = nightsBetween(checkIn, checkOut);
  const selectedEmployee = employees.find((employee) => employee.id === issuedBy);
  const issuedByName = toEmployeeLabel(selectedEmployee) || authManager.userName || issuedBy;

  const canSubmit =
    !allocating &&
    lotId.length > 0 &&
    issuedBy.length > 0 &&
    (allowUnknownPlate || plate.trim().length > 0) &&
    new Date(checkOut).getTime() >= new Date(checkIn).getTime() &&
    (mode === "booking" ? !!selectedBooking : guestName.trim().length > 1);

  const submit = async () => {
    const effectivePlateUnknown = plateUnknown || !plate.trim();

    if (mode === "booking" && selectedBooking) {
      await onAllocate({
        booking: selectedBooking,
        bookingRef: selectedBooking.ref,
        lotId,
        plate: plate.trim() || null,
        plateUnknown: effectivePlateUnknown,
        guestDoesNotKnowPlateYet: effectivePlateUnknown,
        checkIn,
        checkOut,
        issuedBy,
        issuedByName,
        note: note || undefined,
      });
    } else if (mode === "walkin") {
      await onCreateWalkIn({
        guestName,
        email: email || undefined,
        lotId,
        ticketCode: nextVoucherId(),
        plate: plate.trim() || null,
        plateUnknown: plateUnknown || !plate.trim(),
        checkIn,
        checkOut,
        issuedBy,
        issuedByName,
        note: note || undefined,
      });
    }
  };

  const heading = mode === "booking" ? "Assign ticket" : "New walk-in booking";
  const subtitle =
    mode === "booking"
      ? "Draws a ticket from the block and links it to the guest's reservation."
      : "Guest has no pre-booking - create one, then draw a ticket.";

  return (
    <Overlay onClose={onCancel}>
      <div className={s.dialog}>
        <div className={s.head}>
          <div>
            <div className={s.heading}>{heading}</div>
            <div className={s.sub}>{subtitle}</div>
          </div>
          <button onClick={onCancel} className={s.closeBtn}>x</button>
        </div>

        {!isLocked && (
          <div className={s.segmented}>
            <button onClick={() => setMode("booking")} className={cx(s.segBtn, mode === "booking" && s.segOn)}>Match pre-booking</button>
            <button onClick={() => setMode("walkin")} className={cx(s.segBtn, mode === "walkin" && s.segOn)}>Create walk-in</button>
          </div>
        )}

        <div className={s.stack}>
            {mode === "booking" &&
              (isLocked && preselected ? (
                <div className={s.lockCard}>
                  <span className={s.label}>Pre-booking</span>
                  <div className={s.lockName}>{preselected.guestName}</div>
                  <div className={s.lockMeta}>{preselected.email ? preselected.email : ""}</div>
                </div>
              ) : (
                <div>
                  <label className={s.label}>Pre-booking awaiting allocation</label>
                  {awaiting.length === 0 ? (
                    <div className={s.emptyNote}>No pre-bookings awaiting allocation. Switch to walk-in to create one.</div>
                  ) : (
                    <select value={bookingRef} onChange={(e) => setBookingRef(e.target.value)} className={cx(ui.field, s.field)}>
                      {awaiting.map((b) => (
                        <option key={b.ref} value={b.ref}>{b.ref} - {b.guestName}{b.email ? ` - ${b.email}` : ""}</option>
                      ))}
                    </select>
                  )}
                </div>
              ))}

            {mode === "walkin" && (
              <div className={s.grid2}>
                <div>
                  <label className={s.label}>Guest name</label>
                  <input value={guestName} onChange={(e) => setGuestName(e.target.value)} placeholder="e.g. Nick" className={cx(ui.field, s.field)} />
                </div>
                <div>
                  <label className={s.label}>Email (optional)</label>
                  <input value={email} onChange={(e) => setEmail(e.target.value)} placeholder="guest@drivo.com.au" className={cx(ui.field, s.field)} />
                </div>
              </div>
            )}

            <div className={s.lockCard}>
              <label className={s.label}>Lot</label>
              <div className={s.plainValue}>{selectedLot?.name || "No lot assigned"}</div>
            </div>

            <div>
              <label className={s.label}>Plate</label>
              <input value={plate} onChange={(e) => setPlate(e.target.value)} disabled={plateUnknown} placeholder="BXR 44K" className={cx(ui.field, s.field)} />
              {allowUnknownPlate && (
                <label className={s.checkRow}>
                  <input type="checkbox" checked={plateUnknown} onChange={(e) => setPlateUnknown(e.target.checked)} />
                  Guest doesn't know their plate yet - add on the confirmation link
                </label>
              )}
            </div>

            <div className={s.grid2}>
              <div>
                <label className={s.label}>Check-in</label>
                <input
                  type="date"
                  value={checkIn}
                  onChange={(e) => {
                    const v = e.target.value;
                    setCheckIn(v);
                    if (new Date(checkOut).getTime() <= new Date(v).getTime()) {
                      setCheckOut(toDateInput(addDays(new Date(v), 1)));
                    }
                  }}
                  className={cx(ui.field, s.field)}
                />
              </div>
              <div>
                <label className={s.label}>Check-out</label>
                <input type="date" value={checkOut} min={toDateInput(addDays(new Date(checkIn), 1))} onChange={(e) => setCheckOut(e.target.value)} className={cx(ui.field, s.field)} />
              </div>
            </div>

            <div>
              <label className={s.label}>Issued by *</label>
              <select
                value={issuedBy}
                onChange={(e) => setIssuedBy(e.target.value)}
                className={cx(ui.field, s.field)}
                disabled={usersLoading && employees.length === 0}
              >
                <option value="">{usersLoading ? "Loading employees..." : "Select staff member..."}</option>
                {employees.map((employee) => (
                  <option key={employee.id} value={employee.id}>
                    {toEmployeeLabel(employee)}
                  </option>
                ))}
              </select>
            </div>

            <div>
              <label className={s.label}>Note (optional)</label>
              <input value={note} onChange={(e) => setNote(e.target.value)} placeholder="Late arrival, plate to follow, etc." className={cx(ui.field, s.field)} />
            </div>

            {selectedLot && (
              <div className={s.summary}>
                <span>{nights} night{nights === 1 ? "" : "s"}</span>
                <span>{remaining} left in block</span>
              </div>
            )}

            <div className={s.footer}>
              <button onClick={onCancel} className={cx(ui.mutedBtn, s.cancelBtn)}>{cancelLabel}</button>
              <button disabled={!canSubmit} onClick={submit} className={ui.primaryBtn}>
                {allocating ? "Allocating..." : "Allocate ticket"}
              </button>
            </div>
        </div>
      </div>
    </Overlay>
  );
}
